import React from "react";
import { X as XIcon, PlayCircle, Film, Compass } from "lucide-react";

/**
 * TrailerDialog
 *
 * Voce del Centro Guida: presenta il trailer del film "In Time" come
 * riferimento culturale che aiuta a comprendere la filosofia dell'ecosistema
 * TIME. Non è un contenuto promozionale: è parte del percorso introduttivo
 * del prodotto.
 *
 * Design allineato alle altre guide del Centro Guida:
 *  - backdrop nero traslucido con blur
 *  - carta glassmorphica premium bordata di emerald
 *  - tipografia minimale "Apple-like"
 *  - pulsante primario apre il trailer YouTube in nuova scheda
 *  - pulsante secondario "Torna al Centro Guida" riporta all'hub delle guide
 *
 * NOTA: la checkbox "Non mostrare più" è stata rimossa in questa versione.
 * L'unico popup dell'onboarding con quell'opzione è ora quello nella Home.
 */

const TRAILER_URL = "https://www.youtube.com/watch?v=4D3rfKEBxuA";

export default function TrailerDialog({
  open,
  onClose,
  onBack,
}) {
  if (!open) return null;

  const handleClose = () => {
    onClose && onClose();
  };

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
    } else {
      onClose && onClose();
    }
  };

  const handleWatch = () => {
    try {
      window.open(TRAILER_URL, "_blank", "noopener,noreferrer");
    } catch {
      // fallback silenzioso: se il popup viene bloccato non compromettere l'app
    }
  };

  return (
    <div
      data-testid="trailer-dialog-backdrop"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="trailer-dialog-title"
    >
      <div
        className="glass-card w-full max-w-lg p-7 sm:p-9 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="trailer-dialog"
      >
        <button
          type="button"
          onClick={handleClose}
          data-testid="trailer-dialog-close"
          aria-label="Chiudi"
          className="absolute top-4 right-4 p-1.5 rounded-full text-white/40 hover:text-white hover:bg-white/[0.06] transition"
        >
          <XIcon className="w-4 h-4" />
        </button>

        <div className="text-white/90">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-emerald-300/80 mb-3">
            <Film className="w-3 h-3" aria-hidden="true" />
            TIME Inside · Centro Guida
          </div>

          <h2
            id="trailer-dialog-title"
            data-testid="trailer-dialog-title"
            className="text-2xl sm:text-3xl font-extralight text-white mb-5 leading-tight"
          >
            <span className="mr-2" aria-hidden="true">🎬</span>
            Perché un trailer di un film?
          </h2>

          <div className="space-y-4 text-sm sm:text-[15px] text-white/70 font-light leading-relaxed mb-7">
            <p>
              Il film <em className="text-white/90 not-italic">In Time</em> propone
              una domanda affascinante. E se il Tempo fosse la risorsa più
              preziosa che possediamo?
            </p>
            <p>
              TIME nasce da una riflessione diversa, ma ispirata da questa
              stessa idea. Guardare questo breve trailer ti aiuterà a
              comprendere più rapidamente la filosofia che ha dato origine
              all&apos;intero ecosistema TIME.
            </p>
            <p>
              E, se posso darti un consiglio personale, prepara dei popcorn,
              una Coca-Cola e concediti una serata per guardare l&apos;intero
              film insieme alle persone a cui vuoi bene. Potrebbe lasciarti
              qualche riflessione che continuerà a risuonare anche dopo i
              titoli di coda.
            </p>
            <p className="text-emerald-300/90">
              Buona visione… e buon Tempo.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleWatch}
              data-testid="trailer-dialog-watch"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-400 text-black text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-emerald-300 transition-colors"
            >
              <PlayCircle className="w-4 h-4" aria-hidden="true" />
              Guarda il trailer
            </button>
            <button
              type="button"
              onClick={handleBack}
              data-testid="trailer-dialog-back"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/15 text-white/80 text-[11px] tracking-[0.28em] uppercase hover:bg-white/[0.06] hover:text-white transition-colors"
            >
              <Compass className="w-3.5 h-3.5" aria-hidden="true" />
              Torna al Centro Guida
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
